import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { FeatherArrowLeft, FeatherUsers } from '../../components/ui/icons';
import DateSelector from '../../components/DailyReview/DateSelector';
import UserAllocationCard from '../../components/DailyReview/UserAllocationCard';
import dailyReviewService from '../../services/dailyReviewService';
import { getTodayLocalDate } from '../../utils/date';
import './UserAllocationReview.css';

const UserAllocationReview = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [date, setDate] = useState(() => {
        const dateParam = searchParams.get('date');
        return dateParam || getTodayLocalDate();
    });
    const [allocationData, setAllocationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAllocationData();
    }, [projectId, date]);

    const fetchAllocationData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dailyReviewService.getUserAllocation(projectId, date);
            setAllocationData(data);
        } catch (err) {
            console.error('Error fetching allocation data:', err);
            setError('Failed to load user allocation data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (newDate) => {
        setDate(newDate);
    };

    if (loading) {
        return (
            <div className="user-allocation-review-container">
                <div className="user-allocation-review-loading">
                    <div className="loading-spinner" />
                    <p>Loading user allocation data...</p>
                </div>
            </div>
        );
    }

    if (error || !allocationData) {
        return (
            <div className="user-allocation-review-container">
                <div className="user-allocation-review-error">
                    <p>{error || 'Allocation data not found'}</p>
                    <Button onClick={fetchAllocationData}>Retry</Button>
                </div>
            </div>
        );
    }

    const { userAllocations } = allocationData;

    return (
        <div className="user-allocation-review-container">
            <div className="user-allocation-review-header">
                <div className="user-allocation-review-header-top">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={() => navigate(`/daily-work-plan-review/project/${projectId}?date=${date}`)}
                    >
                        Back to Project Review
                    </Button>
                    <div>
                        <h1 className="user-allocation-review-title">User Allocation Review</h1>
                        <p className="user-allocation-review-subtitle">
                            Detailed task allocation and history for all users
                        </p>
                    </div>
                </div>
                <div className="user-allocation-review-header-actions">
                    <DateSelector date={date} onDateChange={handleDateChange} />
                </div>
            </div>

            <div className="user-allocation-review-content">
                <div className="user-allocation-review-summary">
                    <div className="user-allocation-review-summary-item">
                        <FeatherUsers size={20} className="user-allocation-review-summary-icon" />
                        <div>
                            <div className="user-allocation-review-summary-label">Total Users</div>
                            <div className="user-allocation-review-summary-value">{userAllocations.length}</div>
                        </div>
                    </div>
                </div>

                {userAllocations.length > 0 ? (
                    <div className="user-allocation-review-list">
                        {userAllocations.map((allocation, index) => (
                            <UserAllocationCard
                                key={allocation.id || index}
                                user={allocation}
                                taskDetails={allocation.taskDetails || []}
                                taskStats={allocation.taskStats || {}}
                                closedVsAttempted={allocation.closedVsAttempted || { closed: 0, attempted: 0, ratio: 0 }}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="user-allocation-review-empty">
                        <p>No user allocations found for this project on the selected date.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserAllocationReview;

